import React from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * RM CROWN MARKET — fixed bottom navigation
 * Single JSX file, no extra packages or external stylesheets.
 * Existing navigation routes are unchanged.
 */
const NAV_ITEMS = [
  { to: "/", label: "Home", icon: "fa-solid fa-house" },
  { to: "/app-asset", label: "Asset", icon: "fa-solid fa-briefcase" },
  { to: "/withdraw", label: "Withdraw", icon: "fa-solid fa-money-bill-transfer", center: true },
  { to: "/menue", label: "Menu", icon: "fa-solid fa-bars" },
  { to: "/app-profile", label: "Profile", icon: "fa-solid fa-user-circle" },
];

const Footer = () => {
  const { pathname } = useLocation();
  const currentPath = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  return (
    <>
      <style>{`
        :root { --rm-footer-navy: #00191D; --rm-footer-cyan: #16B8C7; }
        html, body, #root { background-color: #00191D; }
        body { padding-bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important; }

        .rm-bottom-nav, .rm-bottom-nav * { box-sizing: border-box; }
        .rm-bottom-nav {
          position: fixed;
          z-index: 99999;
          bottom: 0px;
          left: 4px;
          right: 4px;
          width: calc(100% - 8px);
          height: 76px;
          display: flex;
          align-items: stretch;
          justify-content: space-between;
          border: 1px solid rgba(22,184,199,.30);
          border-top: 2px solid rgba(22,184,199,.78);
          border-radius: 20px;
          background: linear-gradient(160deg, #06272c 0%, #00191D 70%);
          box-shadow: 0 -8px 28px rgba(0,0,0,.22), 0 8px 24px rgba(0,0,0,.24);
          padding: 5px 5px max(5px, env(safe-area-inset-bottom, 0px));
          isolation: isolate;
        }
        .rm-bottom-nav__link {
          position: relative;
          flex: 1 1 0;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          gap: 5px;
          border-radius: 13px;
          text-decoration: none !important;
          color: #b4cbd0 !important;
          font-size: 12px;
          font-weight: 600;
          line-height: 1.2;
          transition: color .18s ease, background .18s ease, transform .18s ease;
          -webkit-tap-highlight-color: transparent;
        }
        .rm-bottom-nav__link i { font-size: 19px; line-height: 1; transition: transform .18s ease; }
        .rm-bottom-nav__link span { white-space: nowrap; }
        .rm-bottom-nav__link:not(.rm-bottom-nav__link--center):hover,
        .rm-bottom-nav__link:not(.rm-bottom-nav__link--center):focus-visible {
          background: rgba(22,184,199,.10);
          color: #fff !important;
        }
        .rm-bottom-nav__link:not(.rm-bottom-nav__link--center).is-active {
          background: rgba(22,184,199,.14);
          color: #55e4ee !important;
        }
        .rm-bottom-nav__link:not(.rm-bottom-nav__link--center).is-active::after {
          content: "";
          position: absolute;
          bottom: 3px;
          width: 17px;
          height: 3px;
          border-radius: 5px;
          background: #16B8C7;
        }
        .rm-bottom-nav__link--center {
          flex: 1 1 0;
          color: #fff !important;
          padding-top: 12px;
          gap: 4px;
          font-weight: 700;
        }
        .rm-bottom-nav__center-icon {
          position: absolute;
          top: -29px;
          left: 50%;
          transform: translateX(-50%);
          width: 59px;
          height: 59px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #37d9e5 0%, #16B8C7 50%, #08798a 100%);
          border: 4px solid #00191D;
          box-shadow: 0 5px 18px rgba(22,184,199,.32);
          color: #00191D;
          transition: transform .18s ease, box-shadow .18s ease;
        }
        .rm-bottom-nav__center-icon i { font-size: 22px; }
        .rm-bottom-nav__link--center > span:last-child { margin-top: 17px; }
        .rm-bottom-nav__link--center:hover .rm-bottom-nav__center-icon,
        .rm-bottom-nav__link--center:focus-visible .rm-bottom-nav__center-icon {
          transform: translateX(-50%) translateY(-3px);
          box-shadow: 0 8px 24px rgba(22,184,199,.45);
        }
        .rm-bottom-nav__link--center.is-active > span:last-child { color: #55e4ee; }
        .rm-bottom-nav__link:focus-visible { outline: 2px solid #55e4ee; outline-offset: 2px; }
        @media (min-width: 900px) {
          .rm-bottom-nav { left: 50%; right: auto; transform: translateX(-50%); max-width: 720px; width: calc(100% - 8px); }
        }
        @media (max-width: 420px) {
          body { padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px)) !important; }
          .rm-bottom-nav { height: 69px; border-radius: 16px; padding: 4px 2px; }
          .rm-bottom-nav__link { font-size: 11px; gap: 4px; }
          .rm-bottom-nav__link i { font-size: 17px; }
          .rm-bottom-nav__center-icon { top: -26px; width: 54px; height: 54px; border-radius: 18px; }
          .rm-bottom-nav__center-icon i { font-size: 20px; }
          .rm-bottom-nav__link--center > span:last-child { margin-top: 15px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .rm-bottom-nav__link, .rm-bottom-nav__link i, .rm-bottom-nav__center-icon { transition: none; }
        }
      `}</style>
      <footer className="rm-bottom-nav" aria-label="Main navigation">
        {NAV_ITEMS.map(({ to, label, icon, center }) => {
          const active = currentPath === to;
          return (
            <Link
              key={to}
              to={to}
              aria-label={label}
              aria-current={active ? "page" : undefined}
              className={`rm-bottom-nav__link${center ? " rm-bottom-nav__link--center" : ""}${active ? " is-active" : ""}`}
            >
              {center ? (
                <span className="rm-bottom-nav__center-icon" aria-hidden="true"><i className={icon} /></span>
              ) : (
                <i className={icon} aria-hidden="true" />
              )}
              <span>{label}</span>
            </Link>
          );
        })}
      </footer>
    </>
  );
};

export default Footer;
